/**
 * FL-132: 案件デモデータ
 *
 * 2025年7月22日（火曜日）を基準とした案件データセット
 * - 過去案件: 完了済み実績データ
 * - 当日案件: 進行中・新規案件（デモの中心）
 * - 未来案件: 予定案件
 *
 * billableDistance について：
 * このデモデータに含まれる billableDistance の値は、概算値です。
 * 本番環境では Google Maps Distance Matrix API を使用して、
 * 正確な距離を計算することを強く推奨します。
 *
 * 現在のデモデータの距離値:
 * - 短距離: 32-45km（東京都内）
 * - 中距離: 48-55km（東京-近郊）
 * - 長距離: 60-68km（東京-県外）
 *
 * 参考: docs/20_guide/208_command_reference.md の Google Maps API 統合を参照
 */

import { Case } from '../../types';
import { DEMO_VEHICLE_MODEL_NAMES } from './vehicleModelData';
// Data generation utilities (not directly used in exports)

// サンプル取引先ID（実際のデモ環境に存在するUUID）
const SAMPLE_PARTNERS = {
  BILLING: {
    AUTO_DEALER_A: '11111111-1111-1111-1111-111111111111',
    AUTO_DEALER_B: '22222222-2222-2222-2222-222222222222',
    AUCTION_HOUSE: '44444444-4444-4444-4444-444444444444',
  },
  PICKUP: {
    AUCTION_HOUSE: '44444444-4444-4444-4444-444444444444',
    INDIVIDUAL_A: '55555555-5555-5555-5555-555555555555',
    INDIVIDUAL_B: '66666666-6666-6666-6666-666666666666',
    AUTO_DEALER_C: '33333333-3333-3333-3333-333333333333',
  },
  DELIVERY: {
    AUTO_DEALER_A: '11111111-1111-1111-1111-111111111111',
    AUTO_DEALER_B: '22222222-2222-2222-2222-222222222222',
    INDIVIDUAL_A: '55555555-5555-5555-5555-555555555555',
    INDIVIDUAL_B: '66666666-6666-6666-6666-666666666666',
  },
} as const;

// サンプル陸送員ID（実際のデータベースUUID）
const SAMPLE_TRANSPORTERS = {
  TRANSPORTER_A: '0a00ad35-df7c-4347-a02e-0b7b378d83e3', // サンプル陸送員A
  TRANSPORTER_B: '90099867-ae35-49f2-9812-fb0d79f817e1', // サンプル陸送員B
  TRANSPORTER_C: 'a998fdf5-4933-4780-8c02-509035a32b93', // サンプル陸送員C
  TRANSPORTER_D: 'c1d2e3f4-a5b6-5c7d-8e9f-01234567890a', // サンプル陸送員D（田中 健太）
  TRANSPORTER_E: 'd2e3f4a5-b6c7-5d8e-9f01-23456789ab0c', // サンプル陸送員E（中村 明美）
  TRANSPORTER_F: '90099867-ae35-49f2-9812-fb0d79f817e1', // サンプル陸送員B (追加案件用)
} as const;

// サンプル車両モデルID（実在するUUID）
const SAMPLE_VEHICLE_MODELS = {
  PRIUS: 'a1b2c3d4-e5f6-5789-abcd-ef0123456789', // トヨタ プリウス
  CROWN: 'c3d4e5f6-a7b8-5901-cdef-0123456789bc', // トヨタ クラウン
  SERENA: 'b8c9d0e1-f2a3-5456-1234-56789ef01234', // 日産 セレナ
  FIT: 'c9d0e1f2-a3b4-5567-2345-6789ef012345', // ホンダ フィット
  CX5: '062fd5ed-b31f-4c57-9bd2-7c43366cedd5', // マツダ CX-5
  FORESTER: 'd0a30064-5aa5-486a-b1f1-7b566426dda4', // スバル フォレスター
} as const;

/**
 * 過去案件データ（完了済み実績）
 * 期間: 2025年6月22日 〜 2025年7月21日
 */
export const HISTORICAL_CASES: Partial<Case>[] = [
  // 6月中旬〜下旬の完了案件（サンプル10件）
  {
    id: 'case-hist-001',
    status: 'completed',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_A,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.AUCTION_HOUSE,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_A,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_A,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.AUCTION_HOUSE,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_A,
    pickupAddress: '東京都江東区有明4-4-4', // 東京オートオークション
    pickupPhone: '03-3799-0001',
    pickupDate: '2025-06-26',
    pickupTimeStart: '09:00',
    pickupTimeEnd: '10:00',
    deliveryAddress: '東京都港区六本木1-1-1', // 株式会社オートディーラーA
    deliveryPhone: '048-825-0001',
    deliveryDate: '2025-06-26',
    deliveryTime: '14:00',
    vehicleModel: SAMPLE_VEHICLE_MODELS.PRIUS,
    vehicleType: '普通車',
    vehiclePlateNumber: '川崎 300 あ 6447',
    price: 35000,
    remarks: '完了済み - 標準的な配送',
    assignedLandTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_A,
  },

  {
    id: 'case-hist-002',
    status: 'completed',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_B,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.INDIVIDUAL_A,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_B,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_B,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.INDIVIDUAL_A,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_B,
    pickupAddress: '東京都世田谷区成城5-5-5', // 個人顧客A（山田 太郎）
    pickupPhone: '045-662-0001',
    pickupDate: '2025-06-29',
    pickupTimeStart: '10:00',
    pickupTimeEnd: '11:00',
    deliveryAddress: '神奈川県横浜市西区みなとみらい2-2-2', // 株式会社オートディーラーB
    deliveryPhone: '03-3464-0001',
    deliveryDate: '2025-06-29',
    deliveryTime: '15:30',
    vehicleModel: DEMO_VEHICLE_MODEL_NAMES.WELFARE_VEHICLE,
    vehicleType: '普通車',
    vehiclePlateNumber: '品川 300 い 2358',
    price: 28000,
    remarks: '完了済み - 福祉車両（個人宅引取）',
    isVehicleInspectionExpired: false,
    assignedLandTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_B,
  },

  // 7月上旬の完了案件
  {
    id: 'case-hist-003',
    status: 'completed',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUCTION_HOUSE,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.AUTO_DEALER_C,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.INDIVIDUAL_B,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUCTION_HOUSE,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.AUTO_DEALER_C,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.INDIVIDUAL_B,
    pickupAddress: '千葉県市川市田尻1-35-20 ホンダディーラー',
    pickupPhone: '047-379-0001',
    pickupDate: '2025-07-14',
    pickupTimeStart: '08:30',
    pickupTimeEnd: '09:30',
    deliveryAddress: '東京都練馬区石神井町3-25-8',
    deliveryPhone: '03-3996-0001',
    deliveryDate: '2025-07-14',
    deliveryTime: '13:00',
    vehicleModel: SAMPLE_VEHICLE_MODELS.FIT,
    vehicleType: '普通車',
    vehiclePlateNumber: '横浜 500 う 7821',
    price: 32000,
    remarks: '完了済み - ディーラー→個人',
    assignedLandTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_C,
  },

  // 7月10日（前日）完了
  {
    id: 'case-hist-004',
    status: 'completed',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_A,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.AUCTION_HOUSE,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_A,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_A,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.AUCTION_HOUSE,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_A,
    pickupAddress: '東京都江東区有明3-7-26 東京ベイオークション',
    pickupPhone: '03-3520-0001',
    pickupDate: '2025-07-21',
    pickupTimeStart: '16:00',
    pickupTimeEnd: '17:00',
    deliveryAddress: '埼玉県川口市本町4-1-8 マツダディーラー',
    deliveryPhone: '048-222-0001',
    deliveryDate: '2025-07-21',
    deliveryTime: '19:00',
    vehicleModel: DEMO_VEHICLE_MODEL_NAMES.CAMPING_VEHICLE,
    vehicleType: 'SUV',
    vehiclePlateNumber: '千葉 300 え 4532',
    price: 38000,
    remarks: '前日完了 - キャンピングカー（夕方配送）',
    assignedLandTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_D,
  },

  // ============================================================================
  // レポート機能デモ用（稼働実績レポート・請求レポート）
  // 複数日にわたる完了案件で、同一陸送員の稼働を可視化
  // ============================================================================

  // 田中健太（TRANSPORTER_D）の7月15日〜19日の完了案件（5日間）
  {
    id: 'case-hist-demo-001',
    status: 'completed',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_A,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.AUCTION_HOUSE,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_A,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_A,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.AUCTION_HOUSE,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_A,
    pickupAddress: '東京都江東区有明4-4-4',
    pickupPhone: '03-3799-0001',
    pickupDate: '2025-07-15',
    pickupTimeStart: '08:00',
    pickupTimeEnd: '09:00',
    deliveryAddress: '東京都港区六本木1-1-1',
    deliveryPhone: '048-825-0001',
    deliveryDate: '2025-07-15',
    deliveryTime: '11:00',
    vehicleModel: SAMPLE_VEHICLE_MODELS.PRIUS,
    vehicleType: '普通車',
    vehiclePlateNumber: '川崎 300 あ 1234',
    price: 35000,
    remarks: 'レポートデモ用 - 7月15日',
    assignedLandTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_D,
    transportFee: 35000,
    billableDistance: 45, // 東京オークション → 六本木 (約45km)
  },

  {
    id: 'case-hist-demo-002',
    status: 'completed',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_B,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.INDIVIDUAL_A,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_B,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_B,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.INDIVIDUAL_A,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_B,
    pickupAddress: '東京都世田谷区成城5-5-5',
    pickupPhone: '045-662-0001',
    pickupDate: '2025-07-15',
    pickupTimeStart: '13:00',
    pickupTimeEnd: '14:00',
    deliveryAddress: '神奈川県横浜市西区みなとみらい2-2-2',
    deliveryPhone: '03-3464-0001',
    deliveryDate: '2025-07-15',
    deliveryTime: '16:30',
    vehicleModel: DEMO_VEHICLE_MODEL_NAMES.WELFARE_VEHICLE,
    vehicleType: '普通車',
    vehiclePlateNumber: '品川 300 い 5678',
    price: 28000,
    remarks: 'レポートデモ用 - 7月15日午後',
    assignedLandTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_D,
    transportFee: 28000,
    billableDistance: 50, // 世田谷区 → 横浜みなとみらい (約50km)
  },

  {
    id: 'case-hist-demo-003',
    status: 'completed',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUCTION_HOUSE,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.AUTO_DEALER_C,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.INDIVIDUAL_B,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUCTION_HOUSE,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.AUTO_DEALER_C,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.INDIVIDUAL_B,
    pickupAddress: '千葉県市川市田尻1-35-20',
    pickupPhone: '047-379-0001',
    pickupDate: '2025-07-16',
    pickupTimeStart: '08:30',
    pickupTimeEnd: '09:30',
    deliveryAddress: '東京都練馬区石神井町3-25-8',
    deliveryPhone: '03-3996-0001',
    deliveryDate: '2025-07-16',
    deliveryTime: '13:00',
    vehicleModel: SAMPLE_VEHICLE_MODELS.FIT,
    vehicleType: '普通車',
    vehiclePlateNumber: '横浜 500 う 9012',
    price: 32000,
    remarks: 'レポートデモ用 - 7月16日',
    assignedLandTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_D,
    transportFee: 32000,
    billableDistance: 38, // 千葉市川 → 東京練馬 (約38km)
  },

  {
    id: 'case-hist-demo-004',
    status: 'completed',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_A,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.AUCTION_HOUSE,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_A,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_A,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.AUCTION_HOUSE,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_A,
    pickupAddress: '東京都江東区有明4-4-4',
    pickupPhone: '03-3799-0001',
    pickupDate: '2025-07-17',
    pickupTimeStart: '09:00',
    pickupTimeEnd: '10:00',
    deliveryAddress: '東京都港区六本木1-1-1',
    deliveryPhone: '048-825-0001',
    deliveryDate: '2025-07-17',
    deliveryTime: '12:30',
    vehicleModel: SAMPLE_VEHICLE_MODELS.CX5,
    vehicleType: 'SUV',
    vehiclePlateNumber: '成田 300 え 3456',
    price: 40000,
    remarks: 'レポートデモ用 - 7月17日',
    assignedLandTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_D,
    transportFee: 40000,
    billableDistance: 45, // 東京オークション → 六本木 (約45km)
  },

  {
    id: 'case-hist-demo-005',
    status: 'completed',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_B,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.INDIVIDUAL_A,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_B,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_B,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.INDIVIDUAL_A,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_B,
    pickupAddress: '東京都練馬区豊玉北5-17-11',
    pickupPhone: '03-3992-0001',
    pickupDate: '2025-07-18',
    pickupTimeStart: '08:00',
    pickupTimeEnd: '09:00',
    deliveryAddress: '埼玉県越谷市南越谷1-15-1',
    deliveryPhone: '048-985-0001',
    deliveryDate: '2025-07-18',
    deliveryTime: '11:30',
    vehicleModel: DEMO_VEHICLE_MODEL_NAMES.HYBRID_TRUCK,
    vehicleType: 'トラック',
    vehiclePlateNumber: '多摩 300 お 7890',
    price: 29000,
    remarks: 'レポートデモ用 - 7月18日',
    assignedLandTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_D,
    transportFee: 29000,
    billableDistance: 42, // 東京練馬 → 埼玉越谷 (約42km)
  },

  {
    id: 'case-hist-demo-006',
    status: 'completed',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUCTION_HOUSE,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.AUTO_DEALER_C,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.INDIVIDUAL_A,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUCTION_HOUSE,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.AUTO_DEALER_C,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.INDIVIDUAL_A,
    pickupAddress: '千葉県柏市柏6-4-22',
    pickupPhone: '04-7167-0001',
    pickupDate: '2025-07-19',
    pickupTimeStart: '14:00',
    pickupTimeEnd: '15:00',
    deliveryAddress: '東京都江戸川区西葛西6-15-10',
    deliveryPhone: '03-3688-0001',
    deliveryDate: '2025-07-19',
    deliveryTime: '17:30',
    vehicleModel: DEMO_VEHICLE_MODEL_NAMES.CLASSIC_CAR,
    vehicleType: '普通車',
    vehiclePlateNumber: '杉並 300 か 1234',
    price: 35000,
    remarks: 'レポートデモ用 - 7月19日',
    assignedLandTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_D,
    transportFee: 35000,
    billableDistance: 32, // 千葉柏 → 東京江戸川 (約32km)
  },

  // 中村明美（TRANSPORTER_E）の7月14日〜18日の完了案件（稼働実績レポート用）
  {
    id: 'case-hist-demo-007',
    status: 'completed',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_A,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.AUCTION_HOUSE,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_A,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_A,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.AUCTION_HOUSE,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_A,
    pickupAddress: '東京都江東区有明4-4-4',
    pickupPhone: '03-3799-0001',
    pickupDate: '2025-07-14',
    pickupTimeStart: '10:00',
    pickupTimeEnd: '11:00',
    deliveryAddress: '東京都港区六本木1-1-1',
    deliveryPhone: '048-825-0001',
    deliveryDate: '2025-07-14',
    deliveryTime: '14:00',
    vehicleModel: SAMPLE_VEHICLE_MODELS.CROWN,
    vehicleType: '普通車',
    vehiclePlateNumber: '川崎 300 か 5678',
    price: 37000,
    remarks: 'レポートデモ用 - 中村明美 7月14日',
    assignedLandTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_E,
    transportFee: 37000,
    billableDistance: 45, // 東京オークション → 六本木 (約45km)
  },

  {
    id: 'case-hist-demo-008',
    status: 'completed',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_B,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.INDIVIDUAL_A,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_B,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_B,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.INDIVIDUAL_A,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_B,
    pickupAddress: '東京都世田谷区成城5-5-5',
    pickupPhone: '045-662-0001',
    pickupDate: '2025-07-16',
    pickupTimeStart: '09:00',
    pickupTimeEnd: '10:00',
    deliveryAddress: '神奈川県横浜市西区みなとみらい2-2-2',
    deliveryPhone: '03-3464-0001',
    deliveryDate: '2025-07-16',
    deliveryTime: '13:30',
    vehicleModel: SAMPLE_VEHICLE_MODELS.SERENA,
    vehicleType: '普通車',
    vehiclePlateNumber: '品川 300 き 9012',
    price: 30000,
    remarks: 'レポートデモ用 - 中村明美 7月16日',
    assignedLandTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_E,
    transportFee: 30000,
    billableDistance: 50, // 世田谷区 → 横浜みなとみらい (約50km)
  },

  {
    id: 'case-hist-demo-009',
    status: 'completed',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUCTION_HOUSE,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.AUTO_DEALER_C,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.INDIVIDUAL_B,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUCTION_HOUSE,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.AUTO_DEALER_C,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.INDIVIDUAL_B,
    pickupAddress: '千葉県市川市田尻1-35-20',
    pickupPhone: '047-379-0001',
    pickupDate: '2025-07-18',
    pickupTimeStart: '07:30',
    pickupTimeEnd: '08:30',
    deliveryAddress: '東京都練馬区石神井町3-25-8',
    deliveryPhone: '03-3996-0001',
    deliveryDate: '2025-07-18',
    deliveryTime: '12:30',
    vehicleModel: SAMPLE_VEHICLE_MODELS.FORESTER,
    vehicleType: 'SUV',
    vehiclePlateNumber: '横浜 500 く 3456',
    price: 34000,
    remarks: 'レポートデモ用 - 中村明美 7月18日',
    assignedLandTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_E,
    transportFee: 34000,
    billableDistance: 38, // 千葉市川 → 東京練馬 (約38km)
  },
];

/**
 * 当日案件データ（7月22日 - デモの中心）
 * ステータス: unassigned, assigned, in_transit
 */
export const TODAY_CASES: Partial<Case>[] = [
  // 朝の案件（08:00-12:00） - 配車済み
  {
    id: 'e1111111-1111-1111-1111-111111111111',
    status: 'assigned',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_A,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.AUCTION_HOUSE,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_A,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_A,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.AUCTION_HOUSE,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_A,
    pickupAddress: '東京都江東区有明4-4-4', // 東京オートオークション
    pickupPhone: '03-3799-0001',
    pickupDate: '2025-07-22',
    pickupTimeStart: '08:00',
    pickupTimeEnd: '09:00',
    deliveryAddress: '東京都港区六本木1-1-1', // 株式会社オートディーラーA
    deliveryPhone: '048-825-0001',
    deliveryDate: '2025-07-22',
    deliveryTime: '11:30',
    vehicleModel: SAMPLE_VEHICLE_MODELS.PRIUS,
    vehicleType: '普通車',
    vehiclePlateNumber: '湘南 300 お 9876',
    price: 35000,
    remarks: '配車済み - 朝一番の案件',
    assignedLandTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_A,
    requester: '田中営業',
    managementNumber: 'MNG-20250711-001',
    transportFee: 35000,
    billableDistance: 45, // 東京オークション → 六本木 (約45km)
  },

  // 午前の案件（10:00-15:30） - 配車済み
  {
    id: 'e2222222-2222-2222-2222-222222222222',
    status: 'assigned',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUCTION_HOUSE,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.AUTO_DEALER_C,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.INDIVIDUAL_B,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUCTION_HOUSE,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.AUTO_DEALER_C,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.INDIVIDUAL_B,
    pickupAddress: '千葉県千葉市中央区中央3-3-3', // 株式会社オートディーラーC
    pickupPhone: '047-379-0001',
    pickupDate: '2025-07-22',
    pickupTimeStart: '10:00',
    pickupTimeEnd: '11:00',
    deliveryAddress: '神奈川県鎌倉市雪ノ下6-6-6', // 個人顧客B（鈴木 花子）
    deliveryPhone: '03-3311-0001',
    deliveryDate: '2025-07-22',
    deliveryTime: '15:30',
    vehicleModel: DEMO_VEHICLE_MODEL_NAMES.ELECTRIC_CAR,
    vehicleType: '普通車',
    vehiclePlateNumber: '習志野 300 か 5643',
    price: 33000,
    remarks: '配車済み - EV車両（充電設備完備）',
    assignedLandTransporterId: SAMPLE_TRANSPORTERS.TRANSPORTER_D,
    requester: '鈴木営業',
    managementNumber: 'MNG-20250711-002',
    transportFee: 33000,
    isVehicleInspectionExpired: false,
    billableDistance: 68, // 千葉市 → 神奈川鎌倉 (約68km)
  },

  // 夕方の案件（17:00-19:30） - 未配車
  {
    id: 'e3333333-3333-3333-3333-333333333333',
    status: 'provisional',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUCTION_HOUSE,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.AUTO_DEALER_C,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.INDIVIDUAL_A,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUCTION_HOUSE,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.AUTO_DEALER_C,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.INDIVIDUAL_A,
    pickupAddress: '千葉県千葉市中央区中央3-3-3', // 株式会社オートディーラーC
    pickupPhone: '047-422-0001',
    pickupDate: '2025-07-22',
    pickupTimeStart: '17:00',
    pickupTimeEnd: '18:00',
    deliveryAddress: '東京都世田谷区成城5-5-5', // 個人顧客A（山田 太郎）
    deliveryPhone: '03-3856-0001',
    deliveryDate: '2025-07-22',
    deliveryTime: '19:30',
    vehicleModel: SAMPLE_VEHICLE_MODELS.CX5,
    vehicleType: 'SUV',
    vehiclePlateNumber: '成田 300 き 8912',
    price: 39000,
    remarks: '未配車 - 夕方配送',
    requester: '小林営業',
    managementNumber: 'MNG-20250711-003',
    transportFee: 39000,
    billableDistance: 45, // 千葉市 → 東京世田谷 (約45km)
  },

  // オークション会場からディーラーへの緊急配送 - 未配車
  {
    id: 'e4444444-4444-4444-4444-444444444444',
    status: 'provisional',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUCTION_HOUSE,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.AUCTION_HOUSE,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_A,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUCTION_HOUSE,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.AUCTION_HOUSE,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_A,
    pickupAddress: '東京都江東区有明4-4-4', // 東京オートオークション
    pickupPhone: '03-4444-4444',
    pickupDate: '2025-07-22',
    pickupTimeStart: '11:00',
    pickupTimeEnd: '12:00',
    deliveryAddress: '東京都港区六本木1-1-1', // 株式会社オートディーラーA
    deliveryPhone: '03-1111-1111',
    deliveryDate: '2025-07-22',
    deliveryTime: '15:00',
    vehicleModel: DEMO_VEHICLE_MODEL_NAMES.SPORTS_CAR,
    vehicleType: '普通車',
    vehiclePlateNumber: '柏 300 く 2134',
    price: 45000,
    remarks: '未配車 - スポーツカー（オークション落札）',
    requester: 'オークション事務局',
    managementNumber: 'MNG-20250711-004',
    transportFee: 45000,
    requiresTemporaryNumber: false,
    billableDistance: 45, // 東京オークション → 六本木 (約45km)
  },

  // オークション会場から個人顧客への直送 - 未配車
  {
    id: 'e5555555-5555-5555-5555-555555555555',
    status: 'provisional',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUCTION_HOUSE,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.AUCTION_HOUSE,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.INDIVIDUAL_A,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUCTION_HOUSE,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.AUCTION_HOUSE,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.INDIVIDUAL_A,
    pickupAddress: '東京都江東区有明4-4-4', // 東京オートオークション
    pickupPhone: '03-4444-4444',
    pickupDate: '2025-07-22',
    pickupTimeStart: '16:00',
    pickupTimeEnd: '17:00',
    deliveryAddress: '東京都世田谷区成城5-5-5', // 個人顧客A（山田 太郎）
    deliveryPhone: '03-5555-5555',
    deliveryDate: '2025-07-22',
    deliveryTime: '19:00',
    vehicleModel: DEMO_VEHICLE_MODEL_NAMES.ACCIDENT_VEHICLE,
    vehicleType: 'SUV',
    vehiclePlateNumber: '野田 300 け 7865',
    price: 38000,
    remarks: '未配車 - 事故車両（オークション落札・個人宅直送）',
    requester: 'オークション事務局',
    managementNumber: 'MNG-20250711-005',
    transportFee: 38000,
    isVehicleInspectionExpired: true,
    requiresTemporaryNumber: true,
    temporaryNumberNotes: '事故により車検切れ、仮ナンバー取得済み',
    billableDistance: 50, // 東京オークション → 東京世田谷 (約50km)
  },
];

/**
 * 未来案件データ（予定）
 * 期間: 2025年7月23日 〜 2025年8月22日
 */
export const SCHEDULED_CASES: Partial<Case>[] = [
  // 翌日（水曜日）の案件
  {
    id: 'case-future-001',
    status: 'provisional',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_A,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.AUCTION_HOUSE,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_A,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_A,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.AUCTION_HOUSE,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_A,
    pickupAddress: '東京都江東区有明4-4-4', // 東京オートオークション
    pickupPhone: '03-3799-0001',
    pickupDate: '2025-07-23',
    pickupTimeStart: '09:00',
    pickupTimeEnd: '10:00',
    deliveryAddress: '東京都港区六本木1-1-1', // 株式会社オートディーラーA
    deliveryPhone: '047-378-0001',
    deliveryDate: '2025-07-23',
    deliveryTime: '13:00',
    vehicleModel: SAMPLE_VEHICLE_MODELS.PRIUS,
    vehicleType: '普通車',
    vehiclePlateNumber: '袖ヶ浦 300 こ 4329',
    price: 37000,
    remarks: '水曜配送予定',
    requester: '週末担当',
    managementNumber: 'MNG-20250712-001',
    transportFee: 37000,
    billableDistance: 45, // 東京オークション → 六本木 (約45km)
  },

  // 翌週金曜日の案件
  {
    id: 'case-future-002',
    status: 'provisional',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_B,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.INDIVIDUAL_A,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_B,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUTO_DEALER_B,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.INDIVIDUAL_A,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.AUTO_DEALER_B,
    pickupAddress: '東京都練馬区豊玉北5-17-11',
    pickupPhone: '03-3992-0001',
    pickupDate: '2025-07-25',
    pickupTimeStart: '08:30',
    pickupTimeEnd: '09:30',
    deliveryAddress: '埼玉県越谷市南越谷1-15-1 日産ディーラー',
    deliveryPhone: '048-985-0001',
    deliveryDate: '2025-07-25',
    deliveryTime: '12:00',
    vehicleModel: DEMO_VEHICLE_MODEL_NAMES.HYBRID_TRUCK,
    vehicleType: 'トラック',
    vehiclePlateNumber: '多摩 300 さ 9156',
    price: 29000,
    remarks: '翌週金曜予定 - ハイブリッド小型トラック',
    requester: '田中営業',
    managementNumber: 'MNG-20250714-001',
    transportFee: 29000,
    requiresTemporaryNumber: false,
    billableDistance: 42, // 東京練馬 → 埼玉越谷 (約42km)
  },

  // 8月上旬の案件
  {
    id: 'case-future-003',
    status: 'provisional',
    billingPartnerId: SAMPLE_PARTNERS.BILLING.AUCTION_HOUSE,
    pickupPartnerId: SAMPLE_PARTNERS.PICKUP.AUTO_DEALER_C,
    deliveryPartnerId: SAMPLE_PARTNERS.DELIVERY.INDIVIDUAL_B,
    billingClientId: SAMPLE_PARTNERS.BILLING.AUCTION_HOUSE,
    pickupClientId: SAMPLE_PARTNERS.PICKUP.AUTO_DEALER_C,
    deliveryClientId: SAMPLE_PARTNERS.DELIVERY.INDIVIDUAL_B,
    pickupAddress: '千葉県柏市柏6-4-22 ホンダディーラー',
    pickupPhone: '04-7167-0001',
    pickupDate: '2025-08-05',
    pickupTimeStart: '14:00',
    pickupTimeEnd: '15:00',
    deliveryAddress: '東京都江戸川区西葛西6-15-10',
    deliveryPhone: '03-3688-0001',
    deliveryDate: '2025-08-05',
    deliveryTime: '17:00',
    vehicleModel: DEMO_VEHICLE_MODEL_NAMES.CLASSIC_CAR,
    vehicleType: '普通車',
    vehiclePlateNumber: '杉並 300 し 3748',
    price: 35000,
    remarks: '8月上旬配送予定 - クラシックカー（旧車）',
    requester: '佐藤営業',
    managementNumber: 'MNG-20250725-001',
    transportFee: 35000,
    requiresTemporaryNumber: false,
    billableDistance: 32, // 千葉柏 → 東京江戸川 (約32km)
  },
];

/**
 * 全デモ案件データ
 */
export const DEMO_CASES = {
  HISTORICAL: HISTORICAL_CASES,
  TODAY: TODAY_CASES,
  SCHEDULED: SCHEDULED_CASES,
} as const;

/**
 * デモ案件データの統計情報
 */
export const DEMO_CASES_STATS = {
  TOTAL_CASES: HISTORICAL_CASES.length + TODAY_CASES.length + SCHEDULED_CASES.length,
  BY_PERIOD: {
    HISTORICAL: HISTORICAL_CASES.length,
    TODAY: TODAY_CASES.length,
    SCHEDULED: SCHEDULED_CASES.length,
  },
  BY_STATUS: {
    COMPLETED: HISTORICAL_CASES.length,
    PROVISIONAL: [...TODAY_CASES, ...SCHEDULED_CASES].filter(c => c.status === 'provisional')
      .length,
    CONFIRMED: [...TODAY_CASES, ...SCHEDULED_CASES].filter(c => c.status === 'confirmed').length,
    ASSIGNED: TODAY_CASES.filter(c => c.status === 'assigned').length,
    IN_TRANSIT: TODAY_CASES.filter(c => c.status === 'in_transit').length,
  },
  TODAY_TIME_DISTRIBUTION: {
    MORNING: TODAY_CASES.filter(c => {
      const hour = parseInt(c.pickupTimeStart?.split(':')[0] || '0');
      return hour >= 8 && hour < 12;
    }).length,
    AFTERNOON: TODAY_CASES.filter(c => {
      const hour = parseInt(c.pickupTimeStart?.split(':')[0] || '0');
      return hour >= 13 && hour < 17;
    }).length,
    EVENING: TODAY_CASES.filter(c => {
      const hour = parseInt(c.pickupTimeStart?.split(':')[0] || '0');
      return hour >= 17 && hour < 20;
    }).length,
  },
  VEHICLE_PLATE_STATS: {
    WITH_PLATE: TODAY_CASES.filter(c => c.vehiclePlateNumber && !c.vehiclePlateNumberNone).length,
    WITHOUT_PLATE: TODAY_CASES.filter(c => c.vehiclePlateNumberNone === true).length,
  },
} as const;

export default DEMO_CASES;
